import { useEffect, useState } from 'react'
import { ShieldCheck, Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  createGrantDraft,
  formatSecretTimestamp,
  grantsForEntry,
  permissionLabel,
  SECRET_GRANT_PERMISSIONS,
  togglePermission,
  ttlDaysToEpochMs,
} from './secret-dashboard-utils'
import type { SecretEntry, SecretGrant, SecretGrantDraft, SecretGrantPermission, SecretGrantTargetType } from './secret-dashboard-types'

interface SecretGrantsPanelProps {
  entry: SecretEntry
  grants: SecretGrant[]
  isDark: boolean
  isCreating: boolean
  onCreateGrant: (draft: SecretGrantDraft & { expiresAt: number | null }) => Promise<void>
  onRevokeGrant: (grant: SecretGrant) => void
}

function inputClass(isDark: boolean) {
  return cn(
    'w-full rounded-md border px-3 py-2 text-base outline-none md:text-sm',
    isDark ? 'border-white/[0.08] bg-surface-2 text-neutral-100 placeholder-neutral-600' : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400',
  )
}

export function SecretGrantsPanel({ entry, grants, isDark, isCreating, onCreateGrant, onRevokeGrant }: SecretGrantsPanelProps) {
  const [draft, setDraft] = useState<SecretGrantDraft>(createGrantDraft(entry))
  const activeGrants = grantsForEntry(grants, entry)

  useEffect(() => {
    setDraft(createGrantDraft(entry))
  }, [entry.id])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    await onCreateGrant({ ...draft, expiresAt: ttlDaysToEpochMs(draft.ttlDays) })
    setDraft(createGrantDraft(entry))
  }

  function updatePermission(permission: SecretGrantPermission) {
    setDraft((current) => ({ ...current, permissions: togglePermission(current.permissions, permission) }))
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4" /> Grants</div>
      <form onSubmit={handleSubmit} className="space-y-2">
        <select className={inputClass(isDark)} value={draft.targetType} onChange={(e) => setDraft((s) => ({ ...s, targetType: e.target.value as SecretGrantTargetType }))}>
          <option value="api_key">api_key</option>
          <option value="user">user</option>
          <option value="agent">agent</option>
          <option value="skill">skill</option>
        </select>
        <input className={inputClass(isDark)} value={draft.targetId} onChange={(e) => setDraft((s) => ({ ...s, targetId: e.target.value }))} placeholder="Target stable ID" required />
        <input className={inputClass(isDark)} value={draft.targetDisplayName} onChange={(e) => setDraft((s) => ({ ...s, targetDisplayName: e.target.value }))} placeholder="Display name" required />
        <div className="grid gap-2 sm:grid-cols-2">
          <input className={inputClass(isDark)} value={draft.keyPattern} onChange={(e) => setDraft((s) => ({ ...s, keyPattern: e.target.value }))} placeholder="Key pattern" required />
          <input className={inputClass(isDark)} value={draft.ttlDays} onChange={(e) => setDraft((s) => ({ ...s, ttlDays: e.target.value }))} placeholder="TTL days optional" inputMode="decimal" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SECRET_GRANT_PERMISSIONS.map((permission) => (
            <label key={permission} className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
              <input type="checkbox" checked={draft.permissions.includes(permission)} onChange={() => updatePermission(permission)} />
              {permission}
            </label>
          ))}
        </div>
        <input className={inputClass(isDark)} value={draft.reason} onChange={(e) => setDraft((s) => ({ ...s, reason: e.target.value }))} placeholder="Reason" />
        <button disabled={isCreating || draft.permissions.length === 0} className="rounded-md bg-neutral-900 px-3 py-2 text-sm text-white disabled:opacity-50">
          {isCreating ? 'Creating...' : 'Create grant'}
        </button>
      </form>
      <div className="space-y-1">
        {activeGrants.length === 0 ? (
          <p className="text-xs text-neutral-500">No active grants match this entry.</p>
        ) : activeGrants.map((grant) => (
          <div key={grant.id} className={cn('flex items-start justify-between gap-2 rounded-md px-2 py-1.5 text-xs', isDark ? 'bg-surface-2 text-neutral-400' : 'bg-neutral-100 text-neutral-600')}>
            <div>
              <div className="font-medium">{grant.targetDisplayName}</div>
              <div>{grant.targetType}:{grant.targetId}</div>
              <div>{grant.keyPattern} · {permissionLabel(grant.permissions)}</div>
              <div>Expires {formatSecretTimestamp(grant.expiresAt)}</div>
            </div>
            <button type="button" onClick={() => onRevokeGrant(grant)} className="rounded p-1 text-red-500 hover:bg-red-500/10" title="Revoke grant" aria-label={`Revoke ${grant.targetDisplayName}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}
